import { EmptyState, formatPersianNumber } from '../../../app/components/foundation';
import { VaniaCardSkeleton, VaniaLoadingSystem } from '../../../app/components/loading';
import type { AnalyticsSnapshot } from '../../analytics/types/analytics.types';

function formatCompactPeriod(period: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;

  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

export interface TenantAnalyticsSectionProps {
  analytics: AnalyticsSnapshot | null;
  loading: boolean;
  error: string | null;
}

export function TenantAnalyticsSection({ analytics, loading, error }: TenantAnalyticsSectionProps) {
  const series = analytics?.time_series ?? [];
  const chartSeries = series.slice(-8);
  const maxOrders = Math.max(...chartSeries.map((item) => item.orders), 1);

  return (
    <div className="vania-tenant-analytics-content-root">
      <div className="vania-tenant-analytics-header">
        <div>
          <h2 className="vania-tenant-workspace-card-title">تحلیل‌ها</h2>
        </div>
        {analytics && <span className="vania-tenant-analytics-scope">نمایش داده‌های همین تننت</span>}
      </div>

      {loading ? (
        <VaniaLoadingSystem mode="content" label="در حال بارگذاری تحلیل‌های تننت">
          <div className="vania-tenant-analytics-loading">
            <VaniaCardSkeleton lines={2} />
            <VaniaCardSkeleton lines={4} />
          </div>
        </VaniaLoadingSystem>
      ) : error ? (
        <p className="vania-tenants-status vania-tenants-status-error" role="alert">{error}</p>
      ) : !analytics ? (
        <EmptyState message="اطلاعات تحلیلی برای این تننت وجود ندارد." />
      ) : (
        <div className="vania-tenant-analytics-content">
          <div className="vania-tenant-analytics-metrics" aria-label="خلاصه تحلیل‌های تننت">
            <div><span>کاربران</span><strong>{formatPersianNumber(analytics.overview.total_users)}</strong></div>
            <div><span>کاربران فعال</span><strong>{formatPersianNumber(analytics.overview.active_users)}</strong></div>
            <div><span>سفارش‌ها</span><strong>{formatPersianNumber(analytics.orders.count)}</strong></div>
            <div><span>درآمد</span><strong>{formatPersianNumber(analytics.overview.revenue)} تومان</strong></div>
          </div>

          <div className="vania-tenant-analytics-trend">
            <div className="vania-tenant-analytics-trend-heading">
              <h3>روند سفارش‌ها</h3>
              <span>{formatPersianNumber(series.length)} بازه</span>
            </div>
            {series.length === 0 ? (
              <p className="vania-tenant-analytics-empty">روندی برای نمایش ثبت نشده است.</p>
            ) : (
              <div className="vania-tenant-analytics-bars" role="img" aria-label="روند سفارش‌های همین تننت بر اساس بازه زمانی">
                {chartSeries.map((item) => (
                  <div className="vania-tenant-analytics-bar-item" key={item.period}>
                    <span className="vania-tenant-analytics-bar-value" title={`${item.period}: ${formatPersianNumber(item.orders)} سفارش`}>
                      {formatPersianNumber(item.orders)}
                    </span>
                    <span className="vania-tenant-analytics-bar-track">
                      <span style={{ height: `${Math.max(8, (item.orders / maxOrders) * 100)}%` }} />
                    </span>
                    <span
                      className="vania-tenant-analytics-bar-label"
                      title={item.period}
                      aria-label={`${item.period}: ${formatPersianNumber(item.orders)} سفارش`}
                    >
                      {formatCompactPeriod(item.period)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
